// Sponsor slice — the plan-level persona's data layer (ADR #12: one slice file per
// page, centralized here). Two independent fetches share it:
//
//   overview  GET /sponsor/overview        the plan-picker landing (no arg)
//   plan      GET /sponsor/plans/{planId}  one plan's dashboard (keyed by planId)
//
// Both carry the arg-aware single-fetch `condition` guard (FRONTEND_RULES §7) so React
// StrictMode's dev double-fire and a genuine remount collapse into one request, while a
// real change of plan — or a retry after failure — still runs. `planBranding.slice` is
// the sibling precedent for a planId-keyed guard.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getSponsorOverview, getSponsorPlan } from '../../services/api/sponsor';
import {
  mapSponsorOverviewResponse,
  mapSponsorPlanResponse,
} from '../../components/modules/sponsor/sponsor.adapters';

export const fetchSponsorOverview = createAsyncThunk(
  'sponsor/fetchOverview',
  async (_, { rejectWithValue }) => {
    try {
      return mapSponsorOverviewResponse(await getSponsorOverview());
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load your plans.');
    }
  },
  // No arg, fetch-once: skip while in flight or already loaded; a retry after a
  // failure still runs.
  {
    condition: (_, { getState }) => (
      !['loading', 'succeeded'].includes(getState().sponsor.overviewStatus)
    ),
  },
);

export const fetchSponsorPlan = createAsyncThunk(
  'sponsor/fetchPlan',
  async (planId, { rejectWithValue }) => {
    try {
      if (!planId) return rejectWithValue('No plan selected.');
      return mapSponsorPlanResponse(await getSponsorPlan(planId));
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load this plan.');
    }
  },
  {
    // Keyed on the planId, never on 'succeeded' alone — switching plans must refetch.
    condition: (planId, { getState }) => {
      const s = getState().sponsor;
      if ((s.planStatus === 'loading' || s.planStatus === 'succeeded') && s.planArg === planId) {
        return false;
      }
      return true;
    },
  },
);

const initialState = {
  overview: null,
  overviewStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  overviewError: null,
  plan: null,
  planStatus: 'idle',
  planError: null,
  planArg: undefined, // the planId the current `plan` is for
};

const sponsorSlice = createSlice({
  name: 'sponsor',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSponsorOverview.pending, (s) => {
        s.overviewStatus = 'loading';
        s.overviewError = null;
      })
      .addCase(fetchSponsorOverview.fulfilled, (s, a) => {
        s.overviewStatus = 'succeeded';
        s.overview = a.payload;
      })
      .addCase(fetchSponsorOverview.rejected, (s, a) => {
        s.overviewStatus = 'failed';
        s.overviewError = a.payload || 'Failed to load your plans';
      })
      .addCase(fetchSponsorPlan.pending, (s, a) => {
        s.planStatus = 'loading';
        s.planError = null;
        s.planArg = a.meta.arg;
        // Drop the previous plan so a plan switch can't paint stale figures under the
        // new plan's name for a frame.
        s.plan = null;
      })
      .addCase(fetchSponsorPlan.fulfilled, (s, a) => {
        s.planStatus = 'succeeded';
        s.plan = a.payload;
      })
      .addCase(fetchSponsorPlan.rejected, (s, a) => {
        s.planStatus = 'failed';
        s.planError = a.payload || 'Failed to load this plan';
      });
  },
});

export const selectSponsorOverview = (state) => state.sponsor.overview;
export const selectSponsorOverviewStatus = (state) => state.sponsor.overviewStatus;
export const selectSponsorOverviewError = (state) => state.sponsor.overviewError;
export const selectSponsorPlan = (state) => state.sponsor.plan;
export const selectSponsorPlanStatus = (state) => state.sponsor.planStatus;
export const selectSponsorPlanError = (state) => state.sponsor.planError;

export default sponsorSlice.reducer;
